"use client";

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "./useAuthStore";
import { SESSION_CHECK_INTERVAL, TOKEN_REFRESH_INTERVAL } from "@/constants";
import { defaultLocale, isLocale } from "@/i18n";
import { withLocale } from "@/helpers";

/**
 * useAuthInitialization Hook
 *
 * Handles auth initialization and background tasks.
 */
export const useAuthInitialization = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    initializeAuth,
    checkAuthSilently,
    refreshAuthSilently,
    isAuthenticated,
    user,
  } = useAuthStore();

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Handle verification redirect (demo role skips verification)
  useEffect(() => {
    if (
      user &&
      !user.onboardingCompleted &&
      user.role !== "demo" &&
      typeof window !== "undefined" &&
      !window.location.pathname.includes("/verification")
    ) {
      const seg = pathname.split("/").filter(Boolean)[0];
      const locale = isLocale(seg) ? seg : defaultLocale;
      navigate(withLocale(locale, "/verification"));
    }
  }, [user, navigate, pathname]);

  // Set up session check interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      checkAuthSilently();
    }, SESSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, checkAuthSilently]);

  // Set up token refresh interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      refreshAuthSilently();
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshAuthSilently]);
};
